import type { Session } from '@rosetta/shared';
import { redirect } from '@tanstack/react-router';
import type { RechercheLogin } from './params-recherche';
import { lireSession, sessionExpiree } from './session';

/**
 * Where the login page sends the user back to once signed in (B5, #13): the page that was asked
 * for, when it is a path of this application (`/resultat/920001?noyau=SGP`), and the input
 * screen otherwise. A value that would leave the application (`//hote`, `https://...`, `/\hote`)
 * or loop on the login page is ignored: the URL is a trust boundary.
 */
export function redirectionSure(redirection: string | undefined): string {
  if (redirection === undefined || !redirection.startsWith('/')) return '/';
  if (redirection.startsWith('//') || redirection.startsWith('/\\')) return '/';
  if (redirection === '/login' || /^\/login(?:[/?#]|$)/.test(redirection)) return '/';
  return redirection;
}

/**
 * The search of `/login` that brings the user back to `href` (the page being left, by default
 * the current one) after signing in, and says why they are there: a session that expired is
 * said on the login page (#13), a visitor who never signed in is not told anything.
 */
export function rechercheVersLogin(
  motif?: 'session-expiree',
  href = `${window.location.pathname}${window.location.search}`,
): RechercheLogin {
  const redirection = redirectionSure(href);
  return {
    ...(redirection === '/' ? {} : { redirection }),
    ...(motif === undefined ? {} : { motif }),
  };
}

/**
 * Session guard, for the `beforeLoad` of a protected route (`/`, `/historique` and
 * `/resultat/**`). Without a valid session (none, unreadable or expired) it redirects to
 * `/login`, with the page asked for (`href`, the location of the route) so that the login page
 * comes back to it (B5, #13), and the reason when the session expired; otherwise it returns the
 * session.
 */
export function exigerSession(href = '/'): Session {
  const expiree = sessionExpiree();
  const session = lireSession();
  if (session === null) {
    throw redirect({
      to: '/login',
      search: rechercheVersLogin(expiree ? 'session-expiree' : undefined, href),
    });
  }
  return session;
}
